import React, { useEffect, useMemo, useState, useCallback } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

// Import services for Sub Bidang
import {
  fetchSubBidangs,
  createSubBidangs,
  updateSubBidangs,
  deleteSubBidangs
} from '../../services/subBidangService';

// Import service for Bidang list
import { listBidangs } from '../../services/bidangService';

// Import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';

const SubBidang = () => {
  document.title = "Sub Bidang";

  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [subBidang, setSubBidang] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allBidangList, setAllBidangList] = useState([]);
  const [filteredBidangList, setFilteredBidangList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });

  // Filter state (no changes needed here)
  const [filters, setFilters] = useState({
    jenisSeleksi: "",
  });

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setSubBidang(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (subBidang && subBidang.id_sub_bidang) {
        // Menggunakan toast.promise untuk operasi delete juga agar konsisten
        const deletePromise = new Promise(async (resolve, reject) => {
            try {
                const response = await deleteSubBidangs(subBidang.id_sub_bidang);
                if (response.success) {
                    resolve("Sub Bidang berhasil dihapus");
                } else {
                    reject(new Error(response.message || "Gagal menghapus Sub Bidang"));
                }
            } catch (error) {
                reject(new Error(error.response?.data?.message || "Gagal menghapus Sub Bidang"));
            }
        });

        toast.promise(deletePromise, {
            pending: 'Sedang menghapus data...',
            success: {
                render({ data }){
                    return `${data}`;
                }
            },
            error: {
                render({ data }){
                    return `${data.message}`;
                }
            }
        });

        // Setelah promise selesai (baik sukses/gagal), refresh data dan tutup modal
        deletePromise.finally(() => {
            fetchData(pagination.page, pagination.per_page, filters);
            handleCloseDeleteModal();
        });
      }
    } catch (error) {
      console.error("Error initiating delete:", error);
      // Fallback toast jika ada error sebelum promise sempat dijalankan
      toast.error("Terjadi kesalahan saat memulai proses penghapusan.");
    }
  };

  const fetchData = async (page = 1, per_page = 10, filterParams = {}) => {
    try {
      setLoading(true);
      const response = await fetchSubBidangs(page, per_page, filterParams);
      setData(response.data || []);

      setPagination({
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
      });

      // Anda bisa menghapus console.log debugging setelah ini berfungsi
      console.log("Updated Pagination State (after fetchData):", {
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
      });

    } catch (error) {
      console.error("Error in fetchData:", error);
      toast.error("Gagal memuat data Sub Bidang");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBidangList = useCallback(async () => {
    try {
      const response = await listBidangs();
      if (response.success) {
        setAllBidangList(response.data);
      } else {
        toast.error(response.message || "Gagal memuat daftar Bidang");
      }
    } catch (error) {
      console.error("Error fetching all bidang list:", error);
      toast.error("Gagal memuat daftar Bidang");
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchAllBidangList();
  }, [fetchAllBidangList]);

  const filterBidangsByJenisSeleksi = useCallback((jenisSeleksi) => {
    if (jenisSeleksi) {
      const filtered = allBidangList.filter(bidang => bidang.jenis_seleksi === jenisSeleksi);
      setFilteredBidangList(filtered);
    } else {
      setFilteredBidangList([]);
    }
  }, [allBidangList]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    fetchData(1, pagination.per_page, {
      ...filters,
      [name]: value
    });
  };

  const clearFilters = () => {
    setFilters({
      jenisSeleksi: "",
    });
    fetchData();
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id_bidang: subBidang?.id_bidang || "",
      sub_bidang: subBidang?.sub_bidang || "",
      jenis_seleksi: subBidang?.jenis_seleksi || "",
      deskripsi: subBidang?.deskripsi || "",
    },

    validationSchema: Yup.object({
      id_bidang: Yup.number().required("Bidang wajib diisi").typeError("Bidang harus dipilih"),
      sub_bidang: Yup.string().required("Nama Sub Bidang wajib diisi"),
      jenis_seleksi: Yup.string().required("Jenis Seleksi wajib diisi"),
      deskripsi: Yup.string().required("Deskripsi wajib diisi"),
    }),

    onSubmit: async (values) => {
      // 1. Buat Promise yang membungkus operasi API Anda
      const submitPromise = new Promise(async (resolve, reject) => {
        try {
          const formData = new FormData();
          formData.append('id_bidang', values.id_bidang);
          formData.append('sub_bidang', values.sub_bidang);
          formData.append('jenis_seleksi', values.jenis_seleksi);
          formData.append('deskripsi', values.deskripsi);

          let response;
          if (isEdit) {
            response = await updateSubBidangs(subBidang.id_sub_bidang, formData);
          } else {
            response = await createSubBidangs(formData);
          }

          if (response.success) {
            resolve(response.message || (isEdit ? "Sub Bidang berhasil diperbarui" : "Sub Bidang berhasil ditambahkan"));
          } else {
            // Penting: Reject dengan objek Error agar pesan bisa diakses di 'data.message'
            reject(new Error(response.message || (response.code === 409 ? "Nama Sub Bidang sudah ada" : "Gagal menyimpan Sub Bidang")));
          }
        } catch (error) {
          console.error("Error in API call for submission:", error);
          // Penting: Reject dengan objek Error dari catch
          reject(new Error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan"));
        }
      });

      // 2. Panggil toast.promise
      toast.promise(submitPromise, {
        pending: 'Sedang menyimpan data...',
        success: {
          render({ data }){
            // 'data' adalah nilai yang di-resolve dari promise
            return `${data}`;
          }
        },
        error: {
          render({ data }){
            // 'data' adalah objek Error yang di-reject dari promise
            return `${data.message}`;
          }
        }
      });

      // 3. Lakukan operasi follow-up (refresh data, tutup modal) setelah promise selesai
      // Menggunakan .finally() untuk memastikan ini dijalankan terlepas dari sukses/gagal
      submitPromise.finally(() => {
        fetchData(pagination.page, pagination.per_page, filters);
        toggleModal();
      });
    },
  });

  const handleModalJenisSeleksiChange = (e) => {
    const { value } = e.target;
    validation.setFieldValue('jenis_seleksi', value);
    validation.setFieldTouched('jenis_seleksi', true);

    filterBidangsByJenisSeleksi(value);
    validation.setFieldValue('id_bidang', '');
    validation.setFieldTouched('id_bidang', false);
  };

  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setSubBidang(null);
      setIsEdit(false);
      validation.resetForm();
      setFilteredBidangList([]);
      // Tidak perlu lagi setToastDisplayed(false);
    } else {
      setModal(true);
      if (!subBidang) {
          setFilteredBidangList([]);
      }
      // Tidak perlu lagi setToastDisplayed(false);
    }
  };

  const handleSubBidangClick = (subBidangData) => {
    setSubBidang(subBidangData);
    setIsEdit(true);

    filterBidangsByJenisSeleksi(subBidangData.jenis_seleksi);

    toggleModal();
  };

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = (subBidangData) => {
    setSubBidang(subBidangData);
    setDeleteModal(true);
  };

  const columns = useMemo(
    () => [
      {
        header: "Bidang",
        accessorKey: "id_bidang",
        enableColumnFilter: false,
        cell: (cellProps) => {
            const bidang = allBidangList.find(b => b.id_bidang === cellProps.getValue());
            return bidang ? bidang.nama_bidang : cellProps.getValue();
        }
      },
      {
        header: "Nama Sub Bidang",
        accessorKey: "sub_bidang",
        enableColumnFilter: false,
      },
      {
        header: 'Jenis Seleksi',
        accessorKey: "jenis_seleksi",
        enableColumnFilter: false,
      },
      {
        header: 'Deskripsi',
        accessorKey: "deskripsi",
        enableColumnFilter: false,
        cell: (cell) => (
          <div style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }}>
            {cell.getValue()}
          </div>
        )
      },
      {
        header: 'Created At',
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const date = new Date(cellProps.getValue());
          return date.toISOString().split('T')[0];
        }
      },
      {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
             <Link
                    to="#"
                    className="btn btn-sm btn-soft-info"
                    onClick={() => {
                        const subBidangData = cellProps.row.original;
                        handleSubBidangClick(subBidangData);
                    }}
                    id={`edittooltip-${cellProps.row.original.id_sub_bidang}`}
                >
            <i className="mdi mdi-pencil-outline" />
            <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id_sub_bidang}`} >
                Edit
            </UncontrolledTooltip>
            </Link>
            <Link
                to="#"
                className="btn btn-sm btn-soft-danger"
                onClick={() => {
                    const subBidangData = cellProps.row.original;
                    onClickDelete(subBidangData);
                }}
                id={`deletetooltip-${cellProps.row.original.id_sub_bidang}`}
                >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_sub_bidang}`}>
                    Delete
                </UncontrolledTooltip>
                </Link>
          </div>
        )
      },
    ],
    [allBidangList, handleSubBidangClick]
  );

  const CustomPagination = () => {
    const { page, last_page, total } = pagination;

    // Untuk debugging, Anda bisa sementara mengomentari baris di bawah ini
    // agar pagination selalu dirender, meskipun hanya ada satu halaman.
    // Pastikan untuk mengembalikan ini setelah debugging selesai.
    // if (last_page <= 1) return null;

    if (total === 0) return null; // Jika tidak ada data sama sekali

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => fetchData(i, pagination.per_page, filters)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return pages;
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * pagination.per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * pagination.per_page, pagination.total)}</span> dari{' '}
          <span className="fw-semibold">{pagination.total}</span> data
        </div>

        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink
                previous
                onClick={() => page > 1 && fetchData(page - 1, pagination.per_page, filters)}
              >
                &lt;
              </PaginationLink>
            </PaginationItem>

            {getPageNumbers()}

            <PaginationItem disabled={page === last_page}>
              <PaginationLink
                next
                onClick={() => page < last_page && fetchData(page + 1, pagination.per_page, filters)}
              >
                &gt;
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };


  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Sub Bidang" breadcrumbItem="List Sub Bidang" />
          {
            loading ? <Spinners setLoading={setLoading} />
              :
            <Row>
                <Col lg="12">
                  <Card>
                    <CardBody className="border-bottom">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0 card-title flex-grow-1">List Sub Bidang</h5>
                        <div className="d-flex align-items-center">
                          <Button color="primary" onClick={toggleModal}>
                            Tambah Sub Bidang
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                    <CardBody>
                      {/* Filter Section (remains the same) */}
                      <div className="mb-4">
                        <Row className="align-items-end">
                          <Col md={3} className="mb-3">
                            <Label htmlFor="jenisSeleksiFilter">Jenis Seleksi</Label>
                            <Input
                              id="jenisSeleksiFilter"
                              type="select"
                              name="jenisSeleksi"
                              value={filters.jenisSeleksi}
                              onChange={handleFilterChange}
                              className="form-select"
                            >
                              <option value="">Semua Jenis Seleksi</option>
                              <option value="CPNS">CPNS</option>
                              <option value="PPPK">PPPK</option>
                              <option value="KEDINASAN">KEDINASAN</option>
                              <option value="TNI">TNI</option>
                              <option value="POLRI">POLRI</option>
                              <option value="SPPI">SPPI</option>
                            </Input>
                          </Col>
                          <Col md={2} className="mb-3">
                            <Label style={{ visibility: 'hidden' }}>Reset Filter</Label>
                            <Button color="danger" outline onClick={clearFilters} className="w-100">
                              <i className="mdi mdi-refresh me-1"></i> Reset
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false}
                        isPagination={false}
                        customPageSize={pagination.per_page}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      />
                      <CustomPagination />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
          }

          {/* Modal for Add/Edit */}
          <Modal isOpen={modal} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal} tag="h4">
              {!!isEdit ? "Edit Sub Bidang" : "Tambah Sub Bidang"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label>Jenis Seleksi</Label>
                      <Input
                        name="jenis_seleksi"
                        type="select"
                        onChange={handleModalJenisSeleksiChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.jenis_seleksi || ""}
                        invalid={!!(validation.touched.jenis_seleksi && validation.errors.jenis_seleksi)}
                      >
                        <option value="">Pilih Jenis Seleksi</option>
                        <option value="CPNS">CPNS</option>
                        <option value="PPPK">PPPK</option>
                        <option value="KEDINASAN">KEDINASAN</option>
                        <option value="TNI">TNI</option>
                        <option value="POLRI">POLRI</option>
                        <option value="SPPI">SPPI</option>
                      </Input>
                      <FormFeedback>{validation.errors.jenis_seleksi}</FormFeedback>
                    </div>

                    <div className="mb-3">
                      <Label>Bidang</Label>
                      <Input
                        name="id_bidang"
                        type="select"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.id_bidang || ""}
                        invalid={!!(validation.touched.id_bidang && validation.errors.id_bidang)}
                        disabled={!validation.values.jenis_seleksi}
                      >
                        <option value="">Pilih Bidang</option>
                        {filteredBidangList.map((bidang) => (
                          <option key={bidang.id_bidang} value={bidang.id_bidang}>
                            {bidang.nama_bidang}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>{validation.errors.id_bidang}</FormFeedback>
                    </div>

                    <div className="mb-3">
                      <Label>Nama Sub Bidang</Label>
                      <Input
                        name="sub_bidang"
                        type="text"
                        placeholder="Nama Sub Bidang"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.sub_bidang || ""}
                        invalid={!!(validation.touched.sub_bidang && validation.errors.sub_bidang)}
                      />
                      <FormFeedback>{validation.errors.sub_bidang}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Deskripsi</Label>
                      <Input
                        name="deskripsi"
                        type="textarea"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.deskripsi || ""}
                        invalid={!!(validation.touched.deskripsi && validation.errors.deskripsi)}
                      />
                      <FormFeedback>{validation.errors.deskripsi}</FormFeedback>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="text-end">
                    <Button color="success" type="submit">
                        {isEdit ? "Update" : "Simpan"}
                    </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}

export default SubBidang;